import {Clickable, Grid, Hide, HStack, Image, Scroll} from 'platform/foundation';
import {Lightbox, useLightbox, useLightboxActionsFactory} from 'platform/lightbox';

import {useEffect, useState} from 'react';

import {tail, uniq} from 'ramda';

import {testIds} from '@dms/routes';
import {thumbnailUrl as thumbnailPlaceholderUrl} from '@dms/shared';

import {useThumbnailImage} from '../../../hooks/useThumbnailImage';
import {ThumbnailImageVehicle} from '../../../types/ThumbnailImageVehicle';

interface GalleryProps {
  sourcingVehicle: ThumbnailImageVehicle;
}

export function Gallery(props: GalleryProps) {
  const actionsFactory = useLightboxActionsFactory();
  const [secondaryGalleryImages, setSecondaryGalleryImages] = useState<
    {url: string; isError: boolean}[]
  >(tail(uniq(props.sourcingVehicle.pictureUrls ?? [])).map((url) => ({url, isError: false})));

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSecondaryGalleryImages(
      tail(uniq(props.sourcingVehicle.pictureUrls ?? [])).map((url) => ({url, isError: false}))
    );
  }, [props.sourcingVehicle.pictureUrls]);

  const mainImage = useThumbnailImage(props.sourcingVehicle, 'firstFromGallery');

  const imagesWithActions = actionsFactory.addShareOnlyActions(
    [
      mainImage.url ?? thumbnailPlaceholderUrl,
      ...secondaryGalleryImages.filter((image) => !image.isError).map((image) => image.url),
    ].map((src) => ({
      id: src,
      url: src.replace('resize', 'get'),
    }))
  );

  const [lightboxControls, {onOpen}] = useLightbox('sourcingVehicleGallery');

  const handleOpen = (index?: number) => {
    onOpen(index ?? 0);
  };

  return (
    <>
      <Grid columns={1} spacing={4}>
        <Clickable onClick={() => handleOpen()}>
          <Image
            key={mainImage.key}
            data-testid={testIds.vehicles.publishing('advertisement')}
            src={mainImage.url}
            ratio="3 / 2"
            fit="cover"
            hasSpinner
            onError={mainImage.onError}
          />
        </Clickable>
        <Hide when={secondaryGalleryImages.filter((image) => !image.isError).length === 0}>
          <Scroll auto width="100%">
            <HStack spacing={1}>
              {secondaryGalleryImages
                .filter((image) => !image.isError)
                .map((image, index) => (
                  <Clickable key={image.url} onClick={() => handleOpen(index + 1)}>
                    <Image
                      data-testid={testIds.vehicles.publishing(`advertisement-[${index}]`)}
                      src={image.url}
                      height={18}
                      width={28}
                      fit="cover"
                      hasSpinner
                      onError={() =>
                        setSecondaryGalleryImages((prev) =>
                          prev.map((prevImage) =>
                            prevImage.url === image.url ? {...prevImage, isError: true} : prevImage
                          )
                        )
                      }
                    />
                  </Clickable>
                ))}
            </HStack>
          </Scroll>
        </Hide>
      </Grid>
      <Lightbox
        data-testid={testIds.vehicles.publishing('advertisement')}
        controls={lightboxControls}
        images={imagesWithActions}
      />
    </>
  );
}
